import { useEffect, useRef, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Plus, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@ncoder/ui';
import {
  useOrgInvitations,
  useOrgRoles,
  useCreateOrgInvitation,
  useResendOrgInvitation,
  useCancelOrgInvitation,
} from '@/hooks/org-admin';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { InvitationRow } from '@/components/molecules/org-admin/InvitationRow';
import { InviteMemberModal } from '@/components/molecules/org-admin/InviteMemberModal';
import type { OrgAdminSelfContext } from '@/components/molecules/org-admin/types';

export interface InvitationsPanelProps {
  self: OrgAdminSelfContext;
}

const SKELETON_ROWS = [{ email: 'w-40' }, { email: 'w-32' }, { email: 'w-48' }] as const;

function SkeletonInvitationRow({ emailWidth, isLast }: { emailWidth: string; isLast: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-4 px-4 py-3 ${!isLast ? 'border-b border-border/60' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className={`h-3.5 rounded-md bg-muted-foreground/10 loading-parallax ${emailWidth}`} />
          <div className="h-5 w-14 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-5 w-16 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
          <div className="h-3 w-24 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <div className="h-8 w-8 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
        <div className="h-8 w-8 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
      </div>
    </div>
  );
}

export function InvitationsPanel({ self }: InvitationsPanelProps) {
  const { data: invitations = [], isLoading } = useOrgInvitations();
  const { data: roles = [] } = useOrgRoles();
  const createInvitation = useCreateOrgInvitation();
  const resendInvitation = useResendOrgInvitation();
  const cancelInvitation = useCancelOrgInvitation();
  const [confirmModal, confirmModalContext] = useConfirmModal();

  const [inviteOpen, setInviteOpen] = useState(false);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (!isLoading) hasAnimatedRef.current = true;
  }, [isLoading]);

  const trail = useTrail(invitations.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
    immediate: hasAnimatedRef.current,
  });

  const skeletonTrail = useTrail(SKELETON_ROWS.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
  });

  const roleNameById = new Map(roles.map((role) => [role.id, role.name]));

  const handleInviteSubmit = async (data: Parameters<typeof createInvitation.mutateAsync>[0]) => {
    try {
      await createInvitation.mutateAsync(data);
      toast.success('Invitation sent');
      setInviteOpen(false);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to send invitation');
    }
  };

  const handleResend = (invitationId: string) => {
    resendInvitation.mutate(invitationId, {
      onSuccess: () => toast.success('Invitation resent'),
      onError: (err) => toast.error((err as Error).message || 'Failed to resend invitation'),
    });
  };

  const handleCancel = async (invitationId: string) => {
    const confirmed = await confirmModal.error({
      title: 'Cancel invitation?',
      content: 'The invitation link will stop working. This does not delete its history.',
      okText: 'Cancel invitation',
      okButtonProps: { variant: 'destructive' },
    });
    if (!confirmed) return;
    cancelInvitation.mutate(invitationId, {
      onSuccess: () => toast.success('Invitation cancelled'),
      onError: (err) => toast.error((err as Error).message || 'Failed to cancel invitation'),
    });
  };

  const canInvite = self.currentUserRole === 'owner' || self.currentUserRole === 'admin';

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Invite people to join the org and assign a badge up front.
        </p>
        <Button size="sm" onClick={() => setInviteOpen(true)} disabled={!canInvite}>
          <Plus className="h-4 w-4" />
          Invite member
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto border-t border-border/60">
        {isLoading ? (
          skeletonTrail.map((style, index) => (
            <animated.div key={index} style={style}>
              <SkeletonInvitationRow
                emailWidth={SKELETON_ROWS[index].email}
                isLast={index === SKELETON_ROWS.length - 1}
              />
            </animated.div>
          ))
        ) : invitations.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-warning/10">
              <Mail className="h-7 w-7 text-warning" />
            </div>
            <p>No invitations sent yet.</p>
          </div>
        ) : (
          trail.map((style, index) => {
            const invitation = invitations[index];
            return (
              <animated.div key={invitation.id} style={style}>
                <InvitationRow
                  invitation={invitation}
                  roleName={invitation.role_id ? roleNameById.get(invitation.role_id) ?? null : null}
                  isResending={resendInvitation.isPending && resendInvitation.variables === invitation.id}
                  isCancelling={cancelInvitation.isPending && cancelInvitation.variables === invitation.id}
                  onResend={handleResend}
                  onCancel={handleCancel}
                />
              </animated.div>
            );
          })
        )}
      </div>

      <InviteMemberModal
        open={inviteOpen}
        roles={roles}
        currentUserRole={self.currentUserRole}
        isSubmitting={createInvitation.isPending}
        onSubmit={handleInviteSubmit}
        onClose={() => setInviteOpen(false)}
      />
      {confirmModalContext}
    </div>
  );
}
